import React from 'react'
import {
  ariaAttr,
  dataAttr,
  isNumber,
  mergeClassnames,
  useCallbackRef,
  useMergeRefs
} from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import type { CloseButtonProps } from '../closeButton'
import { CloseButton } from '../closeButton'

export type NativeInputProps = React.ComponentPropsWithoutRef<'input'>

type ContentClickHandler<T> = (
  key: T,
  e: React.MouseEvent<HTMLDivElement>
) => void

type InputSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface InputProps extends Omit<NativeInputProps, 'size'> {
  className?: string
  contentLeft?: React.ReactNode
  contentRight?: React.ReactNode
  fullWidth?: boolean
  htmlAutocomplete?: NativeInputProps['autoComplete']
  htmlType?: NativeInputProps['type']
  htmlSize?: NativeInputProps['size']
  invalid?: boolean
  isClearable?: boolean
  isContentLeftClickable?: boolean
  isContentRightClickable?: boolean
  label?: string
  labelLeft?: string | React.ReactNode
  labelRight?: string | React.ReactNode
  maxWidth?: number | string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClearClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onContentLeftClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onContentRightClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  readOnly?: boolean
  removeContentLeftStyling?: boolean
  removeContentRightStyling?: boolean
  width?: string | number
  size?: InputSize
}

const simulateChangeEvent = (
  el: HTMLInputElement,
  event: React.MouseEvent<HTMLButtonElement>
): React.ChangeEvent<HTMLInputElement> => {
  return {
    ...event,
    target: el,
    currentTarget: el
  }
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      className,
      contentLeft,
      contentRight,
      disabled = false,
      fullWidth,
      htmlAutocomplete = 'off',
      htmlType = 'text',
      invalid = false,
      isClearable = false,
      isContentLeftClickable = false,
      isContentRightClickable = false,
      label,
      labelLeft,
      labelRight,
      maxWidth,
      name,
      onBlur: _onBlur,
      onChange: _onChange,
      onClearClick: _onClearClick,
      onContentLeftClick: _onContentLeftClick,
      onContentRightClick: _onContentRightClick,
      onFocus: _onFocus,
      placeholder,
      readOnly = false,
      removeContentLeftStyling = false,
      removeContentRightStyling = false,
      required = false,
      value,
      width: _width,
      htmlSize,
      ...rest
    },
    ref
  ) => {
    const [selfValue, setSelfValue] = React.useState<typeof value>(value || '')
    const [isFocused, setIsFocused] = React.useState<boolean>(false)
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const refs = useMergeRefs(ref, inputRef)

    const onFocus = useCallbackRef(_onFocus)
    const onBlur = useCallbackRef(_onBlur)
    const onChange = useCallbackRef(_onChange)
    const onContentLeftClick = useCallbackRef(_onContentLeftClick)
    const onContentRightClick = useCallbackRef(_onContentRightClick)
    const onClearClick = useCallbackRef(_onClearClick)

    const _className = mergeClassnames('mote-input-root', className)

    const isControlled = React.useMemo(() => value !== undefined, [value])

    const width = React.useMemo(() => {
      if (fullWidth) return '100%'
      if (_width) return isNumber(_width) ? `${_width}px` : _width
      return 'initial'
    }, [fullWidth, _width])

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled || readOnly) return

        setSelfValue(event.target.value)
        onChange && onChange(event)
      },
      [disabled, readOnly, onChange]
    )

    const handleFocus = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        onFocus?.(event)
      },
      [onFocus]
    )

    const handleBlur = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)
        onBlur?.(event)
      },
      [onBlur]
    )

    const handleClear = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled || readOnly) return

        event.preventDefault()
        event.stopPropagation()
        event.nativeEvent.stopImmediatePropagation()

        setSelfValue('')
        onClearClick?.(event)

        if (!inputRef.current) return

        const element = inputRef.current

        const changeEvent = simulateChangeEvent(element, event)

        changeEvent.target.value = ''
        onChange?.(changeEvent)
        inputRef.current.focus()
      },
      [disabled, onChange, onClearClick, readOnly]
    )

    const handleContentClick = React.useCallback<
      ContentClickHandler<'left' | 'right'>
    >(
      (key, event) => {
        if (disabled) return

        const action = key === 'left' ? onContentLeftClick : onContentRightClick

        action?.(event)
      },
      [disabled, onContentLeftClick, onContentRightClick]
    )

    React.useEffect(() => {
      if (isControlled) {
        setSelfValue((value as string) || '')
      }
    }, [value])

    const controlledValue = isControlled
      ? { value: selfValue }
      : { defaultValue: rest.defaultValue }

    return (
      <Root
        $size={size}
        className={_className}
        width={width}
        maxWidth={maxWidth}
        data-disabled={dataAttr(disabled)}
      >
        {label && <LabelWrapper>{label}</LabelWrapper>}
        <InputWrapper className='mote-input-wrapper'>
          <InputGroup
            as={label ? 'div' : 'label'}
            className='mote-input-group'
            data-focused={dataAttr(isFocused)}
            data-disabled={dataAttr(disabled)}
            data-invalid={dataAttr(invalid)}
          >
            {labelLeft && (
              <InputLabel className='mote-input__label-left' placement='left'>
                {labelLeft}
              </InputLabel>
            )}
            {contentLeft && (
              <InputContent
                className='mote-input__content-left'
                removeStyles={removeContentLeftStyling}
                isClickable={isContentLeftClickable}
                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                  handleContentClick('left', event)
                }
              >
                {contentLeft}
              </InputContent>
            )}
            <StyledInput
              ref={refs}
              className='mote-input'
              name={name}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              type={htmlType}
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck='false'
              autoComplete={htmlAutocomplete}
              readOnly={readOnly}
              invalid={invalid}
              hasLeftContent={!!contentLeft}
              hasRightContent={!!contentRight}
              aria-invalid={ariaAttr(invalid)}
              aria-required={ariaAttr(required)}
              aria-readonly={ariaAttr(readOnly)}
              aria-placeholder={placeholder || undefined}
              disabled={disabled}
              size={htmlSize}
              {...rest}
              {...controlledValue}
            />
            {isClearable && (
              <InputClearButton
                className='mote-input__clear-button'
                visible={Boolean(selfValue)}
                hasContentRight={!!contentRight}
                disabled={disabled || readOnly}
                onClick={handleClear}
              />
            )}
            {contentRight && (
              <InputContent
                className='mote-input__content-right'
                removeStyles={removeContentRightStyling}
                isClickable={isContentRightClickable}
                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                  handleContentClick('right', event)
                }
              >
                {contentRight}
              </InputContent>
            )}
            {labelRight && (
              <InputLabel className='mote-input__label-right' placement='right'>
                {labelRight}
              </InputLabel>
            )}
          </InputGroup>
        </InputWrapper>
      </Root>
    )
  }
)

if (__DEV__) {
  Input.displayName = 'Input'
}

const Root = styled.div<
  Pick<InputProps, 'width' | 'maxWidth'> & {
    $size: InputSize
  }
>`
  --input-bg-color: ${th.color('white')};
  --input-color: ${th.color('black')};
  --input-placeholder-color: ${th.color('grey.d1')};
  --input-font-weight: ${th.fontWeight('normal')};
  width: ${({ width }) => width};
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  box-sizing: border-box;

  max-width: ${({ maxWidth }) =>
    isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $size }) => {
    switch ($size) {
      case 'xs':
        return css`
          --input-margin: ${th.space(1)};
          --input-padding: ${th.space(1)};
          --input-border-radius: ${th.radius('sm')};
          --input-font-size: ${th.fontSize('xs')};
          --input-line-height: ${th.lineHeight('xs')};
          --input-height-ratio: 1.2;
        `
      case 'sm':
        return css`
          --input-margin: ${th.space(2)};
          --input-padding: ${th.space(1)};
          --input-border-radius: ${th.radius('sm')};
          --input-font-size: ${th.fontSize('sm')};
          --input-line-height: ${th.lineHeight('sm')};
          --input-height-ratio: 1.6;
        `
      case 'md':
        return css`
          --input-margin: ${th.space(2.5)};
          --input-padding: ${th.space(1.5)};
          --input-border-radius: ${th.radius('md')};
          --input-font-size: ${th.fontSize('md')};
          --input-line-height: ${th.lineHeight('md')};
          --input-height-ratio: 2;
        `
      case 'lg':
        return css`
          --input-margin: ${th.space(2.5)};
          --input-padding: ${th.space(1.5)};
          --input-border-radius: ${th.radius('md')};
          --input-font-size: ${th.fontSize('lg')};
          --input-line-height: ${th.lineHeight('lg')};
          --input-height-ratio: 2.4;
        `
      case 'xl':
        return css`
          --input-margin: ${th.space(3)};
          --input-padding: ${th.space(2)};
          --input-border-radius: ${th.radius('lg')};
          --input-font-size: ${th.fontSize('xl')};
          --input-line-height: ${th.lineHeight('xl')};
          --input-height-ratio: 2.8;
        `
      default:
        return
    }
  }};
`

const StyledInput = styled.input<
  Omit<InputProps, 'htmlSize'> & {
    hasLeftContent: boolean
    hasRightContent: boolean
    size: NativeInputProps['size']
  }
>`
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 0 var(--input-padding);
  margin: ${th.space(1)} var(--input-margin);
  font-weight: var(--input-font-weight);
  font-size: var(--input-font-size);
  line-height: var(--input-line-height);
  color: var(--input-color);
  appearance: none;
  border: none;
  border-radius: 0;
  outline: none;
  background: transparent;
  transition: all 150ms ease-out;

  &::placeholder {
    color: var(--input-placeholder-color);
    font-weight: normal;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  ${({ hasLeftContent, hasRightContent }) => {
    if (hasLeftContent)
      return css`
        margin-left: 0;
        padding-left: 0;
      `

    if (hasRightContent)
      return css`
        margin-right: 0;
        padding-right: 0;
      `
  }};
`

const LabelWrapper = styled.label`
  font-weight: 500;
  font-size: var(--input-font-size);
  line-height: var(--input-line-height);
  color: ${th.color('grey.d3')};
  margin-bottom: 8px;
`

const InputWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: var(--input-border-radius);
  height: calc(var(--input-height-ratio) * 1.25rem);
`

const InputGroup = styled.div`
  height: 100%;
  display: inline-flex;
  flex-direction: row;
  flex: 1;
  align-items: center;
  vertical-align: middle;
  user-select: none;
  border-radius: var(--input-border-radius);
  border: 1px solid ${th.color('grey.l2')};
  background-color: var(--input-bg-color);
  transition: all 150ms ease;
  box-sizing: border-box;

  &:hover {
    border-color: ${th.color('grey.d2')};
  }

  &[data-focused] {
    outline: none;
    border: 1px solid ${th.color('primary.base')};
    box-shadow: 0 0 0 1px ${th.color('primary.base')};

    &::placeholder {
      opacity: 0;
    }
  }

  &[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
  }

  &[data-invalid]:not([data-disabled]) {
    border-color: error;

    &:hover {
      border-color: error;
    }

    &[data-focused] {
      border-color: error;
      box-shadow: 0 0 0 1px ${th.color('error')};
    }
  }
`

const InputLabel = styled.span<{ placement: 'left' | 'right' }>`
  position: relative;
  display: inline-flex;
  width: initial;
  height: 100%;
  color: ${th.color('grey.d2')};
  font-weight: normal;
  font-size: var(--input-font-size);
  line-height: var(--input-line-height);
  align-items: center;
  pointer-events: none;
  margin: 0;
  padding: 0 12px;

  &::after {
    display: none;
  }

  &::before {
    display: none;
  }

  ${({ placement }) => {
    switch (placement) {
      case 'left':
        return css`
          border-radius: var(--input-border-radius) 0 0
            var(--input-border-radius);
          padding-right: 0;
        `
      case 'right':
        return css`
          border-radius: 0 var(--input-border-radius) var(--input-border-radius)
            0;
          padding-left: 0;
        `
      default:
        break
    }
  }}
`

const InputContent = styled.span<{
  removeStyles?: boolean
  isClickable?: boolean
}>`
  ${({ removeStyles }) =>
    !removeStyles &&
    css`
      display: flex;
      box-sizing: content-box;
      width: auto;
      height: 100%;
      align-items: center;
      vertical-align: middle;
      margin: 0;
      padding: 0 calc(var(--input-height-ratio) * 0.375rem);
      color: black;
      line-height: var(--input-line-height);
      position: relative;
      cursor: default;
      pointer-events: none;
    `}

  ${({ isClickable }) =>
    isClickable &&
    css`
      pointer-events: auto;
      cursor: pointer;
    `}
`

const InputClearButton = styled(CloseButton).attrs({
  'aria-label': 'Clear'
})<
  CloseButtonProps & {
    visible?: boolean
    hasContentRight?: boolean
  }
>`
  --input-clear-button-size: calc(var(--input-height-ratio) * 0.625rem);
  position: absolute;
  right: calc(var(--input-height-ratio) * 0.1875rem);
  margin: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 100%;
  background-color: ${th.color('grey.d1')};
  color: white;
  opacity: 0;
  visibility: hidden;
  transform: translateX(20%);
  transition-duration: 150ms;
  transition-timing-function: ease;
  transition-property: color, background-color, opacity, transform;

  width: var(--input-clear-button-size);
  height: var(--input-clear-button-size);

  svg {
    width: calc(var(--input-clear-button-size) / 1.5);
    height: calc(var(--input-clear-button-size) / 1.5);
  }

  &:hover {
    color: white;
    background-color: ${th.color('grey.d2')};
  }

  ${({ visible }) =>
    visible &&
    css`
      visibility: visible;
      transform: translateX(0);
      opacity: 1;
    `}

  ${({ hasContentRight }) =>
    hasContentRight &&
    css`
      right: unset;
      position: relative;
      transform: translateX(30%);
    `}
`
